/*
 * Inbox API Routes
 *
 * Features:
 * - Add, query, update, and delete team-owned inbox items
 * - Preserve source provenance, action identity, lifecycle status, seen dates, and trace context
 * - Enforce current-user team membership before reads and writes
 *
 * Implementation:
 * - Uses dbo.Inbox with TeamId ownership and UX_Inbox_TeamId_ActionKey dedupe
 * - Validates account/contact links against the effective item team before writes
 * - Stores AI action trace fields directly on Inbox because the legacy Actions archive is removed
 *
 * Changes:
 * - 2026-05-21: Added Inbox data routes for CRUD operations
 * - 2026-05-29: Added action trace fields and removal statuses; removed Actions-route dependency
 * - 2026-06-21: Added team, account, and contact names to Inbox read/update responses
 * - 2026-06-26: Added ActionTitle and ActionCategory as durable Inbox fields
 * - 2026-06-26: Added bounded summary reads for faster chat tool Inbox searches
 * - 2026-06-26: Removed wildcard LIKE search from Inbox reads
 * - 2026-06-26: Added exact ActionCategory filtering for structured Inbox category search
 * - 2026-06-26: Removed SourcePath from Inbox read filters
 * - 2026-06-26: Added evidenceSummary reads with compact action and memory evidence for chat triage.
 */
import { z } from "zod";
import { DataRoutes } from "./types";

const inboxStatusSchema = z.enum(['open', 'done', 'dismissed', 'removed', 'superseded', 'no_longer_supported']);

const optionalIdFilter = z.number().int().nullable().optional().default(null);
const optionalTextFilter = z.string().trim().min(1).nullable().optional().default(null);
const optionalStatusFilter = inboxStatusSchema.nullable().optional().default(null);
const inboxViewSchema = z.enum(['full', 'summary', 'evidenceSummary']).optional().default('full');
const nullableTextPatch = z.string().trim().min(1).nullable().optional();

const splitSearchTerms = (...values: Array<string | null | undefined>) => {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const value of values) {
    if (!value) {
      continue;
    }
    for (const term of value.split('|')) {
      const normalized = term.trim();
      if (!normalized) {
        continue;
      }
      const key = normalized.toLocaleLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        terms.push(normalized);
      }
    }
  }
  return terms;
};

const termsForFullText = (terms: string[]) =>
  terms.length > 0 ? terms.join(' ') : null;

const patchableFields = [
  'teamId',
  'accountId',
  'contactId',
  'sourceDate',
  'actionTitle',
  'actionCategory',
  'actionText',
  'status',
  'firstSeenDate',
  'lastSeenDate',
  'sourcePath',
  'sourceActionsReportPath',
  'sourceSummaryPath',
  'sourceMarkdownSection',
  'traceMarkdown',
  'insightText',
  'tensionsText',
  'memoryText',
  'evidenceDate',
  'removedDate',
  'removalReason',
  'actionKey'
] as const;

const hasOwn = (data: Record<string, unknown>, key: string) =>
  Object.prototype.hasOwnProperty.call(data, key);

const inboxPostSchema = z.object({
  teamId: z.number().int(),
  accountId: z.number().int().nullable().optional().default(null),
  contactId: z.number().int().nullable().optional().default(null),
  sourceDate: z.coerce.date(),
  actionTitle: z.string().trim().min(1).max(160),
  actionCategory: z.string().trim().min(1).max(80).nullable().optional(),
  actionText: z.string().trim().min(1),
  status: inboxStatusSchema.optional(),
  firstSeenDate: z.coerce.date().nullable().optional(),
  lastSeenDate: z.coerce.date().nullable().optional(),
  sourcePath: z.string().trim().min(1).max(1024),
  sourceActionsReportPath: z.string().trim().min(1).max(1024).nullable().optional(),
  sourceSummaryPath: z.string().trim().min(1).max(1024).nullable().optional(),
  sourceMarkdownSection: z.string().trim().min(1).max(512),
  traceMarkdown: z.string().trim().min(1).max(12000).nullable().optional(),
  insightText: z.string().trim().min(1).max(4000).nullable().optional(),
  tensionsText: z.string().trim().min(1).max(4000).nullable().optional(),
  memoryText: z.string().trim().min(1).max(4000).nullable().optional(),
  evidenceDate: z.coerce.date().nullable().optional(),
  removedDate: z.coerce.date().nullable().optional(),
  removalReason: z.enum(['checked-or-completed', 'closed-status', 'no-supported-actions-in-summary', 'not-present-in-latest-action', 'removed']).nullable().optional(),
  actionKey: z.string().trim().min(1).max(256)
}).strict()
  .transform((data) => ({
    ...data,
    status: data.status ?? 'open',
    firstSeenDate: data.firstSeenDate ?? null,
    lastSeenDate: data.lastSeenDate ?? null,
    sourceActionsReportPath: data.sourceActionsReportPath ?? null,
    sourceSummaryPath: data.sourceSummaryPath ?? null,
    actionCategory: data.actionCategory ?? null,
    traceMarkdown: data.traceMarkdown ?? null,
    insightText: data.insightText ?? null,
    tensionsText: data.tensionsText ?? null,
    memoryText: data.memoryText ?? null,
    evidenceDate: data.evidenceDate ?? null,
    removedDate: data.removedDate ?? null,
    removalReason: data.removalReason ?? null,
    hasStatus: hasOwn(data, 'status') ? 1 : 0,
    hasFirstSeenDate: hasOwn(data, 'firstSeenDate') ? 1 : 0
  }));

const inboxPatchSchema = z.object({
  inboxId: z.number().int(),
  teamId: z.number().int().optional(),
  accountId: z.number().int().nullable().optional(),
  contactId: z.number().int().nullable().optional(),
  sourceDate: z.coerce.date().optional(),
  actionTitle: z.string().trim().min(1).max(160).optional(),
  actionCategory: nullableTextPatch,
  actionText: z.string().trim().min(1).optional(),
  status: inboxStatusSchema.optional(),
  firstSeenDate: z.coerce.date().optional(),
  lastSeenDate: z.coerce.date().optional(),
  sourcePath: z.string().trim().min(1).max(1024).optional(),
  sourceActionsReportPath: nullableTextPatch,
  sourceSummaryPath: nullableTextPatch,
  sourceMarkdownSection: z.string().trim().min(1).max(512).optional(),
  traceMarkdown: z.string().trim().min(1).max(12000).nullable().optional(),
  insightText: nullableTextPatch,
  tensionsText: nullableTextPatch,
  memoryText: nullableTextPatch,
  evidenceDate: z.coerce.date().nullable().optional(),
  removedDate: z.coerce.date().nullable().optional(),
  removalReason: z.enum(['checked-or-completed', 'closed-status', 'no-supported-actions-in-summary', 'not-present-in-latest-action', 'removed']).nullable().optional(),
  actionKey: z.string().trim().min(1).max(256).optional()
}).strict()
  .refine(
    (data) => patchableFields.some((field) => hasOwn(data, field)),
    { message: 'At least one inbox field is required for update' }
  )
  .transform((data) => ({
    ...data,
    teamId: data.teamId ?? null,
    accountId: data.accountId ?? null,
    contactId: data.contactId ?? null,
    sourceDate: data.sourceDate ?? null,
    actionTitle: data.actionTitle ?? null,
    actionCategory: data.actionCategory ?? null,
    actionText: data.actionText ?? null,
    status: data.status ?? null,
    firstSeenDate: data.firstSeenDate ?? null,
    lastSeenDate: data.lastSeenDate ?? null,
    sourcePath: data.sourcePath ?? null,
    sourceActionsReportPath: data.sourceActionsReportPath ?? null,
    sourceSummaryPath: data.sourceSummaryPath ?? null,
    sourceMarkdownSection: data.sourceMarkdownSection ?? null,
    traceMarkdown: data.traceMarkdown ?? null,
    insightText: data.insightText ?? null,
    tensionsText: data.tensionsText ?? null,
    memoryText: data.memoryText ?? null,
    evidenceDate: data.evidenceDate ?? null,
    removedDate: data.removedDate ?? null,
    removalReason: data.removalReason ?? null,
    actionKey: data.actionKey ?? null,
    hasTeamId: hasOwn(data, 'teamId') ? 1 : 0,
    hasAccountId: hasOwn(data, 'accountId') ? 1 : 0,
    hasContactId: hasOwn(data, 'contactId') ? 1 : 0,
    hasSourceDate: hasOwn(data, 'sourceDate') ? 1 : 0,
    hasActionTitle: hasOwn(data, 'actionTitle') ? 1 : 0,
    hasActionCategory: hasOwn(data, 'actionCategory') ? 1 : 0,
    hasActionText: hasOwn(data, 'actionText') ? 1 : 0,
    hasStatus: hasOwn(data, 'status') ? 1 : 0,
    hasFirstSeenDate: hasOwn(data, 'firstSeenDate') ? 1 : 0,
    hasLastSeenDate: hasOwn(data, 'lastSeenDate') ? 1 : 0,
    hasSourcePath: hasOwn(data, 'sourcePath') ? 1 : 0,
    hasSourceActionsReportPath: hasOwn(data, 'sourceActionsReportPath') ? 1 : 0,
    hasSourceSummaryPath: hasOwn(data, 'sourceSummaryPath') ? 1 : 0,
    hasSourceMarkdownSection: hasOwn(data, 'sourceMarkdownSection') ? 1 : 0,
    hasTraceMarkdown: hasOwn(data, 'traceMarkdown') ? 1 : 0,
    hasInsightText: hasOwn(data, 'insightText') ? 1 : 0,
    hasTensionsText: hasOwn(data, 'tensionsText') ? 1 : 0,
    hasMemoryText: hasOwn(data, 'memoryText') ? 1 : 0,
    hasEvidenceDate: hasOwn(data, 'evidenceDate') ? 1 : 0,
    hasRemovedDate: hasOwn(data, 'removedDate') ? 1 : 0,
    hasRemovalReason: hasOwn(data, 'removalReason') ? 1 : 0,
    hasActionKey: hasOwn(data, 'actionKey') ? 1 : 0
  }));

const validateOptionalLinksSql = `
  IF @accountId IS NOT NULL
    AND NOT EXISTS (
      SELECT 1
      FROM [dbo].[Accounts] A
      WHERE A.[ID] = @accountId
        AND (
          A.[TeamId] = @teamId
          OR A.[RlpCompanyId] IS NOT NULL
        )
    )
  BEGIN
    RAISERROR('Account not found for team', 16, 1);
    RETURN;
  END

  IF @contactId IS NOT NULL
    AND NOT EXISTS (
      SELECT 1
      FROM [dbo].[Contacts] C
      INNER JOIN [dbo].[Accounts] A ON C.[AccountId] = A.[ID]
      WHERE C.[Id] = @contactId
        AND (
          A.[TeamId] = @teamId
          OR A.[RlpCompanyId] IS NOT NULL
          OR C.[RlpPersonId] IS NOT NULL
        )
    )
  BEGIN
    RAISERROR('Contact not found for team', 16, 1);
    RETURN;
  END

  IF @accountId IS NOT NULL
    AND @contactId IS NOT NULL
    AND NOT EXISTS (
      SELECT 1
      FROM [dbo].[Contacts] C
      WHERE C.[Id] = @contactId
        AND C.[AccountId] = @accountId
    )
  BEGIN
    RAISERROR('Contact does not belong to account', 16, 1);
    RETURN;
  END
`;

const inboxRoutes: DataRoutes = {
  // Query inbox items for the current user's teams.
  'get /api/data/inbox': {
    schema: z.object({
      teamId: optionalIdFilter,
      status: optionalStatusFilter,
      accountId: optionalIdFilter,
      contactId: optionalIdFilter,
      actionCategory: optionalTextFilter,
      actionKey: optionalTextFilter,
      q: optionalTextFilter,
      signals: optionalTextFilter,
      limit: z.number().int().min(1).max(100).nullable().optional().default(null),
      view: inboxViewSchema
    }).strict()
      .transform((data) => {
        const signalTerms = splitSearchTerms(data.signals);
        return {
          ...data,
          signalFullText: termsForFullText(signalTerms),
          hasSignalSearch: signalTerms.length > 0 ? 1 : 0,
          limit: data.limit ?? 2147483647
        };
      }),
    query: `
      DECLARE @InboxTextMatchesRaw TABLE (
        [InboxId] INT NOT NULL,
        [Rank] INT NOT NULL
      );

      DECLARE @InboxTextMatches TABLE (
        [InboxId] INT NOT NULL PRIMARY KEY,
        [Rank] INT NOT NULL
      );

      DECLARE @InboxFullTextColumnCount INT = 0;

      IF @q IS NOT NULL OR @signalFullText IS NOT NULL
      BEGIN
        SELECT @InboxFullTextColumnCount = COUNT(DISTINCT COL_NAME(FTC.[object_id], FTC.[column_id]))
        FROM sys.fulltext_index_columns FTC
        INNER JOIN sys.fulltext_indexes FTI ON FTC.[object_id] = FTI.[object_id]
        WHERE FTC.[object_id] = OBJECT_ID(N'[dbo].[Inbox]')
          AND FTI.[is_enabled] = 1
          AND COL_NAME(FTC.[object_id], FTC.[column_id]) IN (
            N'ActionTitle',
            N'ActionCategory',
            N'ActionText',
            N'SourceMarkdownSection',
            N'TraceMarkdown',
            N'InsightText',
            N'TensionsText',
            N'MemoryText'
          );

        IF @InboxFullTextColumnCount = 8
        BEGIN
          IF @q IS NOT NULL
          BEGIN
            INSERT INTO @InboxTextMatchesRaw ([InboxId], [Rank])
            EXEC sp_executesql
              N'SELECT [KEY], [RANK]
                FROM FREETEXTTABLE(
                  [dbo].[Inbox],
                  ([ActionTitle], [ActionCategory], [ActionText], [SourceMarkdownSection], [TraceMarkdown], [InsightText], [TensionsText], [MemoryText]),
                  @fullTextQuery
                );',
              N'@fullTextQuery NVARCHAR(4000)',
              @fullTextQuery = @q;
          END;

          IF @signalFullText IS NOT NULL
          BEGIN
            INSERT INTO @InboxTextMatchesRaw ([InboxId], [Rank])
            EXEC sp_executesql
              N'SELECT [KEY], [RANK]
                FROM FREETEXTTABLE(
                  [dbo].[Inbox],
                  ([ActionTitle], [ActionCategory], [ActionText], [SourceMarkdownSection], [TraceMarkdown], [InsightText], [TensionsText], [MemoryText]),
                  @fullTextQuery
                );',
              N'@fullTextQuery NVARCHAR(4000)',
              @fullTextQuery = @signalFullText;
          END;
        END;
      END;

      INSERT INTO @InboxTextMatches ([InboxId], [Rank])
      SELECT [InboxId], MAX([Rank]) AS [Rank]
      FROM @InboxTextMatchesRaw
      GROUP BY [InboxId];

      SELECT TOP (@limit)
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[InboxId] END AS [inboxId],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[TeamId] END AS [teamId],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE CUT.[teamName] END AS [teamName],
        I.[AccountId] AS [accountId],
        A.[Name] AS [accountName],
        I.[ContactId] AS [contactId],
        C.[Name] AS [contactName],
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[SourceDate] END AS [sourceDate],
        I.[ActionTitle] AS [actionTitle],
        I.[ActionCategory] AS [actionCategory],
        CASE
          WHEN @view = N'summary' THEN NULL
          WHEN @view = N'evidenceSummary' THEN LEFT(I.[ActionText], 1200)
          ELSE I.[ActionText]
        END AS [actionText],
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[Status] END AS [status],
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[FirstSeenDate] END AS [firstSeenDate],
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[LastSeenDate] END AS [lastSeenDate],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[SourcePath] END AS [sourcePath],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[SourceActionsReportPath] END AS [sourceActionsReportPath],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[SourceSummaryPath] END AS [sourceSummaryPath],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[SourceMarkdownSection] END AS [sourceMarkdownSection],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[TraceMarkdown] END AS [traceMarkdown],
        I.[InsightText] AS [insightText],
        I.[TensionsText] AS [tensionsText],
        CASE
          WHEN @view = N'summary' THEN NULL
          WHEN @view = N'evidenceSummary' THEN LEFT(I.[MemoryText], 1200)
          ELSE I.[MemoryText]
        END AS [memoryText],
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[EvidenceDate] END AS [evidenceDate],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[RemovedDate] END AS [removedDate],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[RemovalReason] END AS [removalReason],
        CASE WHEN @view = N'summary' THEN NULL ELSE I.[ActionKey] END AS [actionKey],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[CreatedAt] END AS [createdAt],
        CASE WHEN @view IN (N'summary', N'evidenceSummary') THEN NULL ELSE I.[UpdatedAt] END AS [updatedAt]
      FROM [dbo].[Inbox] I
      INNER JOIN [dbo].[CurrentUserAndTeams]() CUT ON I.[TeamId] = CUT.[teamId]
      LEFT JOIN [dbo].[Accounts] A ON I.[AccountId] = A.[ID]
      LEFT JOIN [dbo].[Contacts] C ON I.[ContactId] = C.[Id]
      LEFT JOIN @InboxTextMatches FTS ON I.[InboxId] = FTS.[InboxId]
      WHERE (@teamId IS NULL OR I.[TeamId] = @teamId)
        AND (@status IS NULL OR I.[Status] = @status)
        AND (@accountId IS NULL OR I.[AccountId] = @accountId)
        AND (@contactId IS NULL OR I.[ContactId] = @contactId)
        AND (@actionCategory IS NULL OR I.[ActionCategory] = @actionCategory)
        AND (@actionKey IS NULL OR I.[ActionKey] = @actionKey)
        AND (
          @actionCategory IS NOT NULL
          OR @actionKey IS NOT NULL
          OR (@q IS NULL AND @hasSignalSearch = 0)
          OR FTS.[InboxId] IS NOT NULL
        )
      ORDER BY
        CASE
          WHEN @q IS NULL AND @hasSignalSearch = 0 THEN 0
          ELSE COALESCE(FTS.[Rank], 0)
        END DESC,
        I.[LastSeenDate] DESC,
        I.[InboxId] ASC
    `
  },

  // Upsert an inbox item owned by one of the current user's teams.
  'post /api/data/inbox': {
    schema: inboxPostSchema,
    queryOne: `
      IF NOT EXISTS (SELECT 1 FROM [dbo].[CurrentUserAndTeams]() WHERE [teamId] = @teamId)
      BEGIN
        RAISERROR('Forbidden: team not available to current user', 16, 1);
        RETURN;
      END

      ${validateOptionalLinksSql}

      BEGIN TRY
        BEGIN TRAN;

        IF EXISTS (
          SELECT 1
          FROM [dbo].[Inbox] WITH (UPDLOCK, HOLDLOCK)
          WHERE [TeamId] = @teamId
            AND [ActionKey] = @actionKey
        )
        BEGIN
          UPDATE [dbo].[Inbox]
          SET
            [AccountId] = @accountId,
            [ContactId] = @contactId,
            [SourceDate] = @sourceDate,
            [ActionTitle] = @actionTitle,
            [ActionCategory] = @actionCategory,
            [ActionText] = @actionText,
            [Status] = CASE WHEN @hasStatus = 1 THEN @status ELSE [Status] END,
            [FirstSeenDate] = CASE
              WHEN @hasFirstSeenDate = 1 THEN COALESCE(@firstSeenDate, @sourceDate)
              ELSE [FirstSeenDate]
            END,
            [LastSeenDate] = COALESCE(@lastSeenDate, @sourceDate),
            [SourcePath] = @sourcePath,
            [SourceActionsReportPath] = @sourceActionsReportPath,
            [SourceSummaryPath] = @sourceSummaryPath,
            [SourceMarkdownSection] = @sourceMarkdownSection,
            [TraceMarkdown] = @traceMarkdown,
            [InsightText] = @insightText,
            [TensionsText] = @tensionsText,
            [MemoryText] = @memoryText,
            [EvidenceDate] = @evidenceDate,
            [RemovedDate] = @removedDate,
            [RemovalReason] = @removalReason,
            [UpdatedAt] = sysdatetime()
          OUTPUT
            inserted.[InboxId] AS [inboxId],
            inserted.[TeamId] AS [teamId],
            inserted.[AccountId] AS [accountId],
            inserted.[ContactId] AS [contactId],
            inserted.[SourceDate] AS [sourceDate],
            inserted.[ActionTitle] AS [actionTitle],
            inserted.[ActionCategory] AS [actionCategory],
            inserted.[ActionText] AS [actionText],
            inserted.[Status] AS [status],
            inserted.[FirstSeenDate] AS [firstSeenDate],
            inserted.[LastSeenDate] AS [lastSeenDate],
            inserted.[SourcePath] AS [sourcePath],
            inserted.[SourceActionsReportPath] AS [sourceActionsReportPath],
            inserted.[SourceSummaryPath] AS [sourceSummaryPath],
            inserted.[SourceMarkdownSection] AS [sourceMarkdownSection],
            inserted.[TraceMarkdown] AS [traceMarkdown],
            inserted.[InsightText] AS [insightText],
            inserted.[TensionsText] AS [tensionsText],
            inserted.[MemoryText] AS [memoryText],
            inserted.[EvidenceDate] AS [evidenceDate],
            inserted.[RemovedDate] AS [removedDate],
            inserted.[RemovalReason] AS [removalReason],
            inserted.[ActionKey] AS [actionKey],
            inserted.[CreatedAt] AS [createdAt],
            inserted.[UpdatedAt] AS [updatedAt]
          WHERE [TeamId] = @teamId
            AND [ActionKey] = @actionKey;
        END
        ELSE
        BEGIN
          INSERT INTO [dbo].[Inbox] (
            [TeamId],
            [AccountId],
            [ContactId],
            [SourceDate],
            [ActionTitle],
            [ActionCategory],
            [ActionText],
            [Status],
            [FirstSeenDate],
            [LastSeenDate],
            [SourcePath],
            [SourceActionsReportPath],
            [SourceSummaryPath],
            [SourceMarkdownSection],
            [TraceMarkdown],
            [InsightText],
            [TensionsText],
            [MemoryText],
            [EvidenceDate],
            [RemovedDate],
            [RemovalReason],
            [ActionKey]
          )
          OUTPUT
            inserted.[InboxId] AS [inboxId],
            inserted.[TeamId] AS [teamId],
            inserted.[AccountId] AS [accountId],
            inserted.[ContactId] AS [contactId],
            inserted.[SourceDate] AS [sourceDate],
            inserted.[ActionTitle] AS [actionTitle],
            inserted.[ActionCategory] AS [actionCategory],
            inserted.[ActionText] AS [actionText],
            inserted.[Status] AS [status],
            inserted.[FirstSeenDate] AS [firstSeenDate],
            inserted.[LastSeenDate] AS [lastSeenDate],
            inserted.[SourcePath] AS [sourcePath],
            inserted.[SourceActionsReportPath] AS [sourceActionsReportPath],
            inserted.[SourceSummaryPath] AS [sourceSummaryPath],
            inserted.[SourceMarkdownSection] AS [sourceMarkdownSection],
            inserted.[TraceMarkdown] AS [traceMarkdown],
            inserted.[InsightText] AS [insightText],
            inserted.[TensionsText] AS [tensionsText],
            inserted.[MemoryText] AS [memoryText],
            inserted.[EvidenceDate] AS [evidenceDate],
            inserted.[RemovedDate] AS [removedDate],
            inserted.[RemovalReason] AS [removalReason],
            inserted.[ActionKey] AS [actionKey],
            inserted.[CreatedAt] AS [createdAt],
            inserted.[UpdatedAt] AS [updatedAt]
          VALUES (
            @teamId,
            @accountId,
            @contactId,
            @sourceDate,
            @actionTitle,
            @actionCategory,
            @actionText,
            @status,
            COALESCE(@firstSeenDate, @sourceDate),
            COALESCE(@lastSeenDate, @sourceDate),
            @sourcePath,
            @sourceActionsReportPath,
            @sourceSummaryPath,
            @sourceMarkdownSection,
            @traceMarkdown,
            @insightText,
            @tensionsText,
            @memoryText,
            @evidenceDate,
            @removedDate,
            @removalReason,
            @actionKey
          )
        END

        COMMIT;
      END TRY
      BEGIN CATCH
        IF @@TRANCOUNT > 0
          ROLLBACK;

        THROW;
      END CATCH
    `
  },

  // Update an inbox item by ID.
  'patch /api/data/inbox/:inboxId': {
    schema: inboxPatchSchema,
    queryOne: `
      DECLARE @currentTeamId INT;
      DECLARE @currentAccountId INT;
      DECLARE @currentContactId INT;

      SELECT
        @currentTeamId = I.[TeamId],
        @currentAccountId = I.[AccountId],
        @currentContactId = I.[ContactId]
      FROM [dbo].[Inbox] I
      INNER JOIN [dbo].[CurrentUserAndTeams]() CUT ON I.[TeamId] = CUT.[teamId]
      WHERE I.[InboxId] = @inboxId;

      IF @currentTeamId IS NULL
      BEGIN
        RAISERROR('Inbox item not found', 16, 1);
        RETURN;
      END

      DECLARE @effectiveTeamId INT = CASE WHEN @hasTeamId = 1 THEN @teamId ELSE @currentTeamId END;
      DECLARE @effectiveAccountId INT = CASE WHEN @hasAccountId = 1 THEN @accountId ELSE @currentAccountId END;
      DECLARE @effectiveContactId INT = CASE WHEN @hasContactId = 1 THEN @contactId ELSE @currentContactId END;

      IF NOT EXISTS (SELECT 1 FROM [dbo].[CurrentUserAndTeams]() WHERE [teamId] = @effectiveTeamId)
      BEGIN
        RAISERROR('Forbidden: team not available to current user', 16, 1);
        RETURN;
      END

      IF @effectiveAccountId IS NOT NULL
        AND NOT EXISTS (
          SELECT 1
          FROM [dbo].[Accounts] A
          WHERE A.[ID] = @effectiveAccountId
            AND (
              A.[TeamId] = @effectiveTeamId
              OR A.[RlpCompanyId] IS NOT NULL
            )
        )
      BEGIN
        RAISERROR('Account not found for team', 16, 1);
        RETURN;
      END

      IF @effectiveContactId IS NOT NULL
        AND NOT EXISTS (
          SELECT 1
          FROM [dbo].[Contacts] C
          INNER JOIN [dbo].[Accounts] A ON C.[AccountId] = A.[ID]
          WHERE C.[Id] = @effectiveContactId
            AND (
              A.[TeamId] = @effectiveTeamId
              OR A.[RlpCompanyId] IS NOT NULL
              OR C.[RlpPersonId] IS NOT NULL
            )
        )
      BEGIN
        RAISERROR('Contact not found for team', 16, 1);
        RETURN;
      END

      IF @effectiveAccountId IS NOT NULL
        AND @effectiveContactId IS NOT NULL
        AND NOT EXISTS (
          SELECT 1
          FROM [dbo].[Contacts] C
          WHERE C.[Id] = @effectiveContactId
            AND C.[AccountId] = @effectiveAccountId
        )
      BEGIN
        RAISERROR('Contact does not belong to account', 16, 1);
        RETURN;
      END

      UPDATE [dbo].[Inbox]
      SET
        [TeamId] = CASE WHEN @hasTeamId = 1 THEN @teamId ELSE [TeamId] END,
        [AccountId] = CASE WHEN @hasAccountId = 1 THEN @accountId ELSE [AccountId] END,
        [ContactId] = CASE WHEN @hasContactId = 1 THEN @contactId ELSE [ContactId] END,
        [SourceDate] = CASE WHEN @hasSourceDate = 1 THEN @sourceDate ELSE [SourceDate] END,
        [ActionTitle] = CASE WHEN @hasActionTitle = 1 THEN @actionTitle ELSE [ActionTitle] END,
        [ActionCategory] = CASE WHEN @hasActionCategory = 1 THEN @actionCategory ELSE [ActionCategory] END,
        [ActionText] = CASE WHEN @hasActionText = 1 THEN @actionText ELSE [ActionText] END,
        [Status] = CASE WHEN @hasStatus = 1 THEN @status ELSE [Status] END,
        [FirstSeenDate] = CASE WHEN @hasFirstSeenDate = 1 THEN @firstSeenDate ELSE [FirstSeenDate] END,
        [LastSeenDate] = CASE WHEN @hasLastSeenDate = 1 THEN @lastSeenDate ELSE [LastSeenDate] END,
        [SourcePath] = CASE WHEN @hasSourcePath = 1 THEN @sourcePath ELSE [SourcePath] END,
        [SourceActionsReportPath] = CASE WHEN @hasSourceActionsReportPath = 1 THEN @sourceActionsReportPath ELSE [SourceActionsReportPath] END,
        [SourceSummaryPath] = CASE WHEN @hasSourceSummaryPath = 1 THEN @sourceSummaryPath ELSE [SourceSummaryPath] END,
        [SourceMarkdownSection] = CASE WHEN @hasSourceMarkdownSection = 1 THEN @sourceMarkdownSection ELSE [SourceMarkdownSection] END,
        [TraceMarkdown] = CASE WHEN @hasTraceMarkdown = 1 THEN @traceMarkdown ELSE [TraceMarkdown] END,
        [InsightText] = CASE WHEN @hasInsightText = 1 THEN @insightText ELSE [InsightText] END,
        [TensionsText] = CASE WHEN @hasTensionsText = 1 THEN @tensionsText ELSE [TensionsText] END,
        [MemoryText] = CASE WHEN @hasMemoryText = 1 THEN @memoryText ELSE [MemoryText] END,
        [EvidenceDate] = CASE WHEN @hasEvidenceDate = 1 THEN @evidenceDate ELSE [EvidenceDate] END,
        [RemovedDate] = CASE WHEN @hasRemovedDate = 1 THEN @removedDate ELSE [RemovedDate] END,
        [RemovalReason] = CASE WHEN @hasRemovalReason = 1 THEN @removalReason ELSE [RemovalReason] END,
        [ActionKey] = CASE WHEN @hasActionKey = 1 THEN @actionKey ELSE [ActionKey] END,
        [UpdatedAt] = sysdatetime()
      WHERE [InboxId] = @inboxId
        AND EXISTS (
          SELECT 1
          FROM [dbo].[CurrentUserAndTeams]() CUT
          WHERE CUT.[teamId] = [dbo].[Inbox].[TeamId]
        );

      SELECT
        I.[InboxId] AS [inboxId],
        I.[TeamId] AS [teamId],
        CUT.[teamName] AS [teamName],
        I.[AccountId] AS [accountId],
        A.[Name] AS [accountName],
        I.[ContactId] AS [contactId],
        C.[Name] AS [contactName],
        I.[SourceDate] AS [sourceDate],
        I.[ActionTitle] AS [actionTitle],
        I.[ActionCategory] AS [actionCategory],
        I.[ActionText] AS [actionText],
        I.[Status] AS [status],
        I.[FirstSeenDate] AS [firstSeenDate],
        I.[LastSeenDate] AS [lastSeenDate],
        I.[SourcePath] AS [sourcePath],
        I.[SourceActionsReportPath] AS [sourceActionsReportPath],
        I.[SourceSummaryPath] AS [sourceSummaryPath],
        I.[SourceMarkdownSection] AS [sourceMarkdownSection],
        I.[TraceMarkdown] AS [traceMarkdown],
        I.[InsightText] AS [insightText],
        I.[TensionsText] AS [tensionsText],
        I.[MemoryText] AS [memoryText],
        I.[EvidenceDate] AS [evidenceDate],
        I.[RemovedDate] AS [removedDate],
        I.[RemovalReason] AS [removalReason],
        I.[ActionKey] AS [actionKey],
        I.[CreatedAt] AS [createdAt],
        I.[UpdatedAt] AS [updatedAt]
      FROM [dbo].[Inbox] I
      INNER JOIN [dbo].[CurrentUserAndTeams]() CUT ON I.[TeamId] = CUT.[teamId]
      LEFT JOIN [dbo].[Accounts] A ON I.[AccountId] = A.[ID]
      LEFT JOIN [dbo].[Contacts] C ON I.[ContactId] = C.[Id]
      WHERE I.[InboxId] = @inboxId
    `
  },

  // Delete an inbox item by ID.
  'delete /api/data/inbox/:inboxId': {
    schema: z.object({
      inboxId: z.number().int()
    }).strict(),
    query: `
      IF NOT EXISTS (
        SELECT 1
        FROM [dbo].[Inbox] I
        INNER JOIN [dbo].[CurrentUserAndTeams]() CUT ON I.[TeamId] = CUT.[teamId]
        WHERE I.[InboxId] = @inboxId
      )
      BEGIN
        RAISERROR('Inbox item not found', 16, 1);
        RETURN;
      END

      DELETE FROM [dbo].[Inbox]
      WHERE [InboxId] = @inboxId
        AND EXISTS (
          SELECT 1
          FROM [dbo].[CurrentUserAndTeams]() CUT
          WHERE CUT.[teamId] = [dbo].[Inbox].[TeamId]
        )
    `
  }
};

export default inboxRoutes;
